import { db } from '@core/persistence/db';

// Durable record, on the EXISTING (linking) device, that a newly-linked device
// is owed chat admission (MLS `addMembers` into every conversation) plus a
// history hand-off. Persisted in IndexedDB so the reconcile is NOT tied to the
// Settings modal / tab lifecycle: if the user closes the modal — or the whole
// tab — before the new device finishes link-verify and gets admitted, the
// reconcile resumes on the next app load (`resumePendingOutgoingLink`) instead
// of leaving the new device with zero synced conversations.
//
// `admittedHex` accumulates the conversations the new device has already been
// admitted to, so a resume only admits the remainder — re-adding an existing
// member would create a duplicate MLS leaf. The record is cleared once a
// reconcile pass admits the device to every conversation it considered.

const KEY = '__linking.outgoingPending.v1';

export interface PendingOutgoingLinkRecord {
  accountId: string;
  newDeviceId: string;
  newDevicePub?: string;
  selfDeviceId?: string;
  admittedHex: string[];
}

export async function savePendingOutgoingLink(
  record: PendingOutgoingLinkRecord,
): Promise<void> {
  try {
    await db.settings_kv.put({ key: KEY, value: record, updatedAt: Date.now() });
  } catch {
    /* best-effort — IndexedDB may be unavailable (private mode, eviction) */
  }
}

export async function getPendingOutgoingLink(): Promise<PendingOutgoingLinkRecord | null> {
  try {
    const rec = await db.settings_kv.get(KEY);
    return (rec?.value as PendingOutgoingLinkRecord | undefined) ?? null;
  } catch {
    return null;
  }
}

/** Merge newly-admitted conversation ids into the persisted record (no-op if
 *  there is no current record). */
export async function mergePendingOutgoingLinkAdmitted(
  hexes: string[],
): Promise<void> {
  if (hexes.length === 0) return;
  const current = await getPendingOutgoingLink();
  if (current === null) return;
  const seen = new Set(current.admittedHex);
  for (const h of hexes) seen.add(h);
  await savePendingOutgoingLink({ ...current, admittedHex: Array.from(seen) });
}

export async function clearPendingOutgoingLink(): Promise<void> {
  try {
    await db.settings_kv.delete(KEY);
  } catch {
    /* best-effort */
  }
}
